// קובץ: InvestmentPathTreeByLiquidity.tsx
import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view";
import { InvestmentPathDetail, Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";
import { GroupedLiquiditySummary, MimshakGroupByService } from "../../../services/MimshakGroupByService";
import { v4 as uuidv4 } from "uuid";
import { formatCurrency, formatDateCategoryWithAge, formatPercentageRound } from "../../../utils/namesFormater";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";
import { buildTreeItemLabelString, CustomTreeItem } from "../../../shared/UIParts/CustomTreeItem";
import { MimshakService } from "../../../services/mimshakService";
import { colorAdjustedProductType } from "../../../utils/colorEnumFormatter";
import { Contact } from "../../../interfaces/DTO/crm/contact";

interface InvestmentPathTreeByLiquidityProps {
  products: Product[];
  fundData?: FundDataBasic[];
  contacts: Contact[];
}



export const InvestmentPathTreeByLiquidity: React.FC<InvestmentPathTreeByLiquidityProps> = ({
  products,
  fundData,
  contacts,
}) => {
  const validProducts = useMemo(() => {
    return products.filter(product =>
      product.policies_or_accounts.some(policy =>
        MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths).some(path =>
          fundData?.some(f => f.fund_id === path.path_code)
        )
      )
    );
  }, [products, fundData]);

  const groupedSummaries: GroupedLiquiditySummary[] = useMemo(() => {
    const raw = MimshakGroupByService.groupLiquiditySummaryByYear(validProducts);
    return MimshakGroupByService.mergeRetirementBuckets(raw, contacts.map((c) => c.date_of_birth));
  }, [validProducts, contacts]);


const treeData = useMemo(() => {
  return groupedSummaries.map(({ liquidityDateCategory, objectList }) => {
    // ✅ קיבוץ לפי adjusted_product_type אם קיים, אחרת product_type
    const groupedByProductType: Record<string, typeof objectList> = {};
    objectList.forEach((item) => {
      const product = products.find((p) =>
        p.policies_or_accounts.some((pol) => pol.policy_number === item.policy_number)
      );
      const effectiveType = String(product?.details.adjusted_product_type ?? item.product_type);
      if (!groupedByProductType[effectiveType]) groupedByProductType[effectiveType] = [];
      groupedByProductType[effectiveType].push(item);
    });

    const children = Object.entries(groupedByProductType).map(([productType, items]) => {
      const productTypeChildren = items.map((item) => {
        const product = products.find((p) =>
          p.policies_or_accounts.some((pol) => pol.policy_number === item.policy_number)
        );
        if (!product) return null;

        const policy = product.policies_or_accounts.find(
          (p) => p.policy_number === item.policy_number
        );
        if (!policy) return null;

        const mergedPaths = MimshakService.mergeDuplicateInvestmentPathsByPathCode(policy.investment_paths);

        const effectiveTypeNum = product.details.adjusted_product_type ?? product.details.product_type;

        return mergedPaths
          .filter((path) =>
              fundData?.some((f) => f.fund_id === path.path_code)
            ) // ✅ רק paths שיש להם fund תואם
          .map((path) => {
            const fund = fundData?.find((f) => f.fund_id === path.path_code);
            if (!fund) return null;
            return {
              id: uuidv4(),
              label: buildTreeItemLabelString({
                primary: path.path_name,
                secondary: formatCurrency(path.accumulated_amount ?? 0),
                chip: formatPercentageRound(fund?.stock_market_percent!) || "⚠️",
                grayText: policy.policy_number,
                colorHex: colorAdjustedProductType(effectiveTypeNum),
              }),
            };
          });
      }).flat().filter(Boolean);

      const totalForType = items.reduce((sum, obj) => sum + obj.amount, 0);
      const allPaths: InvestmentPathDetail[] = items.flatMap((item) => {
        const product = products.find((p) =>
          p.policies_or_accounts.some((pol) => pol.policy_number === item.policy_number)
        );
        const policy = product?.policies_or_accounts.find(
          (p) => p.policy_number === item.policy_number
        );
        return policy ? MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths) : [];
      });
      const exposure = MimshakService.calculateWeightedExposureFromPaths(allPaths, fundData);
      const showWarning = exposure.excludedPaths.length > 0;

      const typeNum = Number(productType);
      return {
        id: `productType-${uuidv4()}`,
        label: buildTreeItemLabelString({
          primary: formatAdjustedProductType(typeNum),
          secondary: formatCurrency(totalForType),
          chip: `${(exposure.stockExposure.average * 100).toFixed(0)}%${showWarning ? " ⚠️" : ""}`,
          colorHex: colorAdjustedProductType(typeNum),
        }),
        children: productTypeChildren,
      };
    });

    const total = objectList.reduce((sum, obj) => sum + obj.amount, 0);
    const allPathsForCategory: InvestmentPathDetail[] = objectList.flatMap((item) => {
      const product = products.find((p) =>
        p.policies_or_accounts.some((pol) => pol.policy_number === item.policy_number)
      );
      const policy = product?.policies_or_accounts.find(
        (p) => p.policy_number === item.policy_number
      );
      return policy ? MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths) : [];
    });

    const exposure = MimshakService.calculateWeightedExposureFromPaths(allPathsForCategory, fundData);
    const totalExcludedAmount = exposure.excludedPaths.reduce(
    (sum, item) => sum + item.excludedAmount,
    0
  );
    const showWarning = exposure.excludedPaths.length > 0 && totalExcludedAmount>0 ;

    const ageDisplay = formatDateCategoryWithAge(
      liquidityDateCategory,
      contacts.map((u) => u.date_of_birth)
    );

    return {
        id: `liquidity-${uuidv4()}`,
      label: buildTreeItemLabelString({
        primary: ageDisplay.ageText ?? "-",
        secondary: formatCurrency(total),
        chip: `${(exposure.stockExposure.average * 100).toFixed(0)}%${showWarning ? " ⚠️" : ""}`,
        grayText: ageDisplay.label,
      }),
      children,
    };
  });
}, [groupedSummaries, validProducts, fundData, contacts]);


  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        עץ לפי מועדי נזילות
      </Typography>

      <RichTreeView
        items={treeData}
        getItemId={(item) => item.id}
        getItemLabel={(item) => item.label}
        slots={{
          item: (props: any) => <CustomTreeItem {...props} hideCheckbox />,
        }}
      />
    </Box>
  );
};
